import type ApplicationInstance from "@ember/application/instance";
import * as Sentry from "@sentry/ember";
import type { Metric, User } from "../types";
import { isMedicUser, isPatientUser } from "../types";
import { resetDatadogUser, setDatadogUser } from "../utils/datadog";
import {
  initializeSentry,
  resetSentryUser,
  setSentrySpanAttributes,
  setSentryTags,
  setSentryUser
} from "../utils/sentry";

export default class ControlTower {
  /** Will force the monitoring to send data no matter what is the current environment */
  public static forceMonitoring: boolean = false;
  private static environment?: string;
  private static currentUser: User | undefined;

  static initialize({
    environment,
    sentryOptions
  }: {
    environment: string;
    sentryOptions?: Sentry.BrowserOptions;
  }) {
    ControlTower.environment = environment;
    initializeSentry(environment, sentryOptions);
  }

  /**
   * Sentry 11 does not instrument the app by itself anymore.
   * The idle timeout keeps the 5 s that Sentry 10 used for the Ember transitions.
   */
  static instrumentPerformance(appInstance: ApplicationInstance) {
    if (!Sentry.isInitialized()) return;

    Sentry.instrumentAppInstancePerformance(appInstance, { idleTimeout: 5000 });
  }

  static captureException(e: unknown) {
    Sentry.captureException(e);
  }

  static captureMessage(msg: string) {
    Sentry.captureMessage(msg);
  }

  static async resetUser() {
    resetSentryUser();
    await resetDatadogUser();
  }

  static async setUser(user: User) {
    ControlTower.currentUser = user;

    // whitelist the user attributes we want to send to Sentry and DD
    const { class: cls, id, uuid } = user;
    const baseAttrs = { id: `${cls}-${id}`, class: cls, uuid };
    let attrs;

    if (isMedicUser(user)) {
      // Normalise to a definite boolean: the source value may be absent
      // (e.g. when restored from a token) and must never be undefined here.
      const is_super_user = user.is_super_user ?? false;

      attrs = {
        role: user.role,
        is_super_user,
        first_health_centre_uuid: user.first_health_centre_uuid
      };
    } else if (isPatientUser(user)) {
      attrs = {
        health_centre_id: user.health_centre_id?.toString(),
        health_centre_uuid: user.health_centre_uuid
      };
    }

    const usr = { ...baseAttrs, ...attrs };

    setSentryUser(usr);
    await setDatadogUser(usr);
  }

  static submitMetrics(metrics: Metric[]) {
    if (!this.isMonitoringEnabled) return;

    const updatedMetrics = ControlTower.injectDefaultTags(metrics);
    setSentrySpanAttributes(updatedMetrics);
    setSentryTags(updatedMetrics);
  }

  private static get isMonitoringEnabled(): boolean {
    return (
      ControlTower.forceMonitoring ||
      (Boolean(this.environment) &&
        !["test", "development", "dev"].includes(ControlTower.environment!))
    );
  }

  static enableSentryReplay() {
    // only enable replay if not already enabled
    if (Sentry.getReplay()) {
      return;
    }
    Sentry.addIntegration(Sentry.replayIntegration());
  }

  private static injectDefaultTags(metrics: Metric[]): Metric[] {
    return metrics.map(metric => {
      metric.tags = metric.tags || [];

      if (ControlTower.environment) {
        metric.tags.push(`env:${ControlTower.environment}`);
      }

      if (ControlTower.currentUser) {
        metric.tags.push(`user_type:${ControlTower.currentUser.class}`);
        metric.tags.push(`user_id:${ControlTower.currentUser.id}`);
      }

      return metric;
    });
  }
}
